import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PartnerPartnerDetails, SubPartnerAccount } from 'vopay';
import usePartnerTypeLabels from '@/hooks/usePartnerTypeLabels';
import useProgramTermFormats from '@/hooks/useProgramTermFormats';
import { pickDetail, renderDetailValue } from '@/components/Entities/UI/DetailValue/detailValue';
import CopyableValue from '@/components/Entities/UI/CopyableValue/CopyableValue';

export type PartnerAccountInfoRow = {
  label: string;
  value: ReactNode;
};

/**
 * The Account Info rows (spec 5.6.1): who the partner account is and the program terms it is on,
 * rendered by the drawer's card and the partner page's Details tab alike. The name and ID come from
 * the list row, so they never wait on the details request; the rest renders a skeleton until the
 * details arrive and a dash if they fail. `details` is undefined while loading, null when the
 * request failed.
 */
export default function usePartnerAccountInfoRows(
  partner: SubPartnerAccount,
  details: PartnerPartnerDetails | null | undefined,
): PartnerAccountInfoRow[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.PartnerAccountInfo' });
  const { resolvePartnerTypeLabel } = usePartnerTypeLabels();
  const { formatRevShare, formatAmount } = useProgramTermFormats();

  // Loading, failed, missing, or the value — the shared renderer's four states; a field the
  // partner account has no entry for yet reads as the given placeholder.
  const renderDetailField = (
    pick: (current: PartnerPartnerDetails) => string | null,
    placeholder?: string,
  ): ReactNode => renderDetailValue(pickDetail(details, pick), placeholder);

  // The fee is money in the partner account's own currency, per month.
  const revShare = renderDetailField((current) => current.RevShare && formatRevShare(Number(current.RevShare)));

  const minimumProgramFee = renderDetailField(
    (current) =>
      current.MinimumProgramFee &&
      t('minimumProgramFeeValue', {
        defaultValue: '{{currency}} {{amount}} / month',
        currency: current.Currency,
        amount: formatAmount(Number(current.MinimumProgramFee)),
      }),
  );

  // The row that does not apply to this partner account's program is not rendered (spec 5.6.1);
  // while loading, or when the request failed, neither can be told apart yet, so both show.
  const showRevShare = !details || details.RevShare !== null;
  const showMinimumProgramFee = !details || details.MinimumProgramFee !== null;

  return [
    {
      label: t('accountName', { defaultValue: 'Account Name' }),
      value: partner.Name,
    },
    {
      label: t('accountID', { defaultValue: 'Account ID' }),
      value: <CopyableValue value={partner.PartnerAccountID} />,
    },
    {
      label: t('accountNumber', { defaultValue: 'Account Number' }),
      // Generated on activation; until then the row says so rather than showing a dash.
      value: renderDetailField(
        (current) => current.AccountNumber,
        details?.IsActive === false ? t('assignedAtActivation', { defaultValue: 'Assigned at activation' }) : undefined,
      ),
    },
    {
      label: t('partnerType', { defaultValue: 'Partner Type' }),
      value: renderDetailField((current) => resolvePartnerTypeLabel(current.PartnerTypeLabel || current.PartnerType)),
    },
    ...(showRevShare
      ? [
          {
            label: t('revenueShare', { defaultValue: 'Revenue Share' }),
            value: revShare,
          },
        ]
      : []),
    ...(showMinimumProgramFee
      ? [
          {
            label: t('minimumProgramFee', { defaultValue: 'Minimum Program Fee' }),
            value: minimumProgramFee,
          },
        ]
      : []),
  ];
}
